/**
 * Zod schemas for dancer-published performances.
 *
 * Mirrors the DB CHECK constraints from
 * `20260516101500_create_performances_table.sql` so Zod validation rejects
 * the same set of invalid inputs the database would.
 *
 * Used by server actions in
 * `packages/features/artists/src/server/actions/performances.actions.ts`.
 */
import { z } from 'zod';

export const performanceTypeSchema = z.enum([
  'performance',
  'gala',
  'tour',
  'workshop',
  'appearance',
]);

export type PerformanceTypeInput = z.infer<typeof performanceTypeSchema>;

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
  error: 'validation:invalidDate',
});

const nullableTrimmedText = (maxLength: number) =>
  z
    .string()
    .trim()
    .max(maxLength)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const urlOrNull = z
  .string()
  .trim()
  .pipe(
    z.url({
      error: 'validation:invalidUrl',
    }),
  )
  .refine((v) => v.startsWith('http://') || v.startsWith('https://'), {
    error: 'validation:invalidUrl',
  })
  .transform((v) => v as string)
  .or(z.literal('').transform(() => null))
  .nullable()
  .optional();

const countryCodeSchema = z
  .string()
  .trim()
  .length(2, {
    error: 'validation:rules.countryCodeMustBeIso31661',
  })
  .toUpperCase()
  .or(z.literal('').transform(() => null))
  .nullable()
  .optional();

/**
 * Insert schema. All optional fields default to null on save.
 */
export const performanceInsertSchema = z
  .object({
    professional_profile_id: z.guid({
      error: 'validation:invalidId',
    }),
    title: z
      .string()
      .trim()
      .min(1, {
        error: 'validation:required',
      })
      .max(200, {
        error: 'validation:rules.titleMustBe200CharactersOrFewer',
      }),
    type: performanceTypeSchema.default('performance'),
    description: nullableTrimmedText(1000),
    start_date: isoDateSchema,
    end_date: isoDateSchema.nullable().optional(),
    venue: nullableTrimmedText(200),
    city: nullableTrimmedText(100),
    country_code: countryCodeSchema,
    ticket_url: urlOrNull,
    choreographer: nullableTrimmedText(200),
    // BAL-248 §10b: optional role/part (e.g. "Kitri"). Column 20260604000000.
    role: nullableTrimmedText(200),
    comment: nullableTrimmedText(1000),
  })
  .refine(
    (v) => {
      if (!v.end_date) return true;
      return v.end_date >= v.start_date;
    },
    {
      path: ['end_date'],
      error: 'validation:rules.endDateMustBeOnOrAfter',
    },
  );

export type PerformanceInsertInput = z.input<typeof performanceInsertSchema>;
export type PerformanceInsertOutput = z.output<typeof performanceInsertSchema>;

/**
 * Update schema. All fields optional; same cross-field check as insert.
 */
export const performanceUpdateSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, {
        error: 'validation:required',
      })
      .max(200, {
        error: 'validation:rules.titleMustBe200CharactersOrFewer',
      })
      .optional(),
    type: performanceTypeSchema.optional(),
    description: nullableTrimmedText(1000),
    start_date: isoDateSchema.optional(),
    end_date: isoDateSchema.nullable().optional(),
    venue: nullableTrimmedText(200),
    city: nullableTrimmedText(100),
    country_code: countryCodeSchema,
    ticket_url: urlOrNull,
    choreographer: nullableTrimmedText(200),
    // BAL-248 §10b: optional role/part (e.g. "Kitri"). Column 20260604000000.
    role: nullableTrimmedText(200),
    comment: nullableTrimmedText(1000),
  })
  .refine(
    (v) => {
      if (!v.end_date || !v.start_date) return true;
      return v.end_date >= v.start_date;
    },
    {
      path: ['end_date'],
      error: 'validation:rules.endDateMustBeOnOrAfter',
    },
  );

export type PerformanceUpdateInput = z.input<typeof performanceUpdateSchema>;
export type PerformanceUpdateOutput = z.output<typeof performanceUpdateSchema>;
